import { useCallback, useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { fetchHeatmapBbox, type HeatmapPoint } from '../api/heatmap';
import MapLegend, { type LegendMetric } from '../components/MapLegend';

type Metric = 'rsrp' | 'rsrq' | 'sinr' | 'download_mbps' | 'mos';
type Period = 'all' | '7d' | '24h' | '1h';
type Operator = '' | 'ORANGE' | 'SFR' | 'BOUYGUES TELECOM' | 'FREE MOBILE';

const PERIOD_TO_SINCE_MS: Record<Period, number | null> = {
  'all': null,
  '7d':  7 * 24 * 60 * 60 * 1000,
  '24h': 24 * 60 * 60 * 1000,
  '1h':  60 * 60 * 1000,
};

function normalize(metric: Metric, value: number): number {
  // returns 0..1 for heatmap weighting (1 = "good")
  switch (metric) {
    case 'rsrp': return Math.max(0, Math.min(1, (value + 130) / 50));   // -130..-80
    case 'rsrq': return Math.max(0, Math.min(1, (value + 20) / 17));    // -20..-3
    case 'sinr': return Math.max(0, Math.min(1, (value + 5) / 35));     // -5..30
    case 'download_mbps': return Math.max(0, Math.min(1, value / 500));
    case 'mos': return Math.max(0, Math.min(1, (value - 1) / 4));
    default: return 0.5;
  }
}

export default function MapPage() {
  const ref = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const loadedRef = useRef(false);
  const fetchTimerRef = useRef<number | null>(null);

  const [metric, setMetric]     = useState<Metric>('rsrp');
  const [period, setPeriod]     = useState<Period>('all');
  const [operator, setOperator] = useState<Operator>('');
  const [loading, setLoading]   = useState(false);
  const [count, setCount]       = useState(0);

  const queryAndUpdate = useCallback(async () => {
    const map = mapRef.current;
    if (!map || !loadedRef.current) return;
    const b = map.getBounds();
    const sinceMs = PERIOD_TO_SINCE_MS[period];
    setLoading(true);
    try {
      const data = await fetchHeatmapBbox({
        minLat: b.getSouth(), minLon: b.getWest(),
        maxLat: b.getNorth(), maxLon: b.getEast(),
        metric,
        since: sinceMs ? new Date(Date.now() - sinceMs).toISOString() : undefined,
        operator: operator || undefined,
      });
      setCount(data.count);
      const features = data.points.map((p: HeatmapPoint) => ({
        type: 'Feature' as const,
        geometry: { type: 'Point' as const, coordinates: [p.lon, p.lat] as [number, number] },
        properties: { value: normalize(metric, p.value), raw: p.value, n: p.n },
      }));
      (map.getSource('heatmap') as maplibregl.GeoJSONSource).setData({
        type: 'FeatureCollection',
        features,
      });
    } catch (e) {
      console.warn('heatmap fetch failed', e);
    } finally {
      setLoading(false);
    }
  }, [metric, period, operator]);

  const scheduleFetch = useCallback(() => {
    if (fetchTimerRef.current != null) window.clearTimeout(fetchTimerRef.current);
    fetchTimerRef.current = window.setTimeout(() => {
      queryAndUpdate();
    }, 600);
  }, [queryAndUpdate]);

  useEffect(() => {
    if (!ref.current) return;
    const map = new maplibregl.Map({
      container: ref.current,
      style: {
        version: 8,
        sources: {
          'carto-dark': {
            type: 'raster',
            tiles: [
              'https://a.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
              'https://b.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
              'https://c.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
              'https://d.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
            ],
            tileSize: 256,
            attribution: '© OpenStreetMap © CARTO',
          },
        },
        layers: [{ id: 'carto-dark', type: 'raster', source: 'carto-dark' }],
      },
      center: [2.35, 46.8],
      zoom: 5.5,
    });
    mapRef.current = map;
    // Force resize after first paint — container measurements settle late inside flex.
    setTimeout(() => map.resize(), 100);
    setTimeout(() => map.resize(), 500);
    map.on('load', () => {
      map.addSource('heatmap', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({
        id: 'heatmap-layer',
        type: 'heatmap',
        source: 'heatmap',
        maxzoom: 22,
        paint: {
          'heatmap-weight': ['interpolate', ['linear'], ['get', 'value'], 0, 0, 1, 1],
          'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 4, 8, 10, 22, 18, 42, 22, 60],
          'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 4, 0.7, 18, 2],
          'heatmap-opacity': ['interpolate', ['linear'], ['zoom'], 4, 0.75, 14, 0.7, 18, 0.45],
          'heatmap-color': [
            'interpolate', ['linear'], ['heatmap-density'],
            0,   'rgba(0,0,0,0)',
            0.2, '#ef4444',
            0.5, '#facc15',
            0.8, '#22c55e',
          ],
        },
      });
      // Circle fallback: visible at high zoom when heatmap fades.
      map.addLayer({
        id: 'heatmap-points',
        type: 'circle',
        source: 'heatmap',
        minzoom: 13,
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 13, 3, 18, 8, 22, 14],
          'circle-color': [
            'interpolate', ['linear'], ['get', 'value'],
            0,   '#ef4444',
            0.5, '#facc15',
            1,   '#22c55e',
          ],
          'circle-opacity': ['interpolate', ['linear'], ['zoom'], 13, 0, 15, 0.55, 18, 0.85],
          'circle-stroke-color': '#0b1220',
          'circle-stroke-width': 1,
        },
      });
      loadedRef.current = true;
      queryAndUpdate();
    });
    map.on('moveend', scheduleFetch);
    return () => { map.remove(); mapRef.current = null; loadedRef.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (loadedRef.current) queryAndUpdate();
  }, [metric, period, operator, queryAndUpdate]);

  return (
    <div style={{ position: 'absolute', inset: 0 }}>
      <div ref={ref} style={{ position: 'absolute', inset: 0 }} />
      <div className="border-b border-slate-800 bg-slate-950 flex items-center gap-3 px-4 text-sm" style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 48, zIndex: 5 }}>
        <select
          value={metric}
          onChange={(e) => setMetric(e.target.value as Metric)}
          className="bg-slate-900 border border-slate-700 rounded-md px-2 py-1 text-slate-200"
        >
          <option value="rsrp">RSRP</option>
          <option value="rsrq">RSRQ</option>
          <option value="sinr">SINR</option>
          <option value="download_mbps">Download Mbps</option>
          <option value="mos">MOS</option>
        </select>

        <select
          value={operator}
          onChange={(e) => setOperator(e.target.value as Operator)}
          className="bg-slate-900 border border-slate-700 rounded-md px-2 py-1 text-slate-200"
        >
          <option value="">All operators</option>
          <option value="ORANGE">Orange</option>
          <option value="SFR">SFR</option>
          <option value="BOUYGUES TELECOM">Bouygues Telecom</option>
          <option value="FREE MOBILE">Free Mobile</option>
        </select>

        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value as Period)}
          className="bg-slate-900 border border-slate-700 rounded-md px-2 py-1 text-slate-200"
        >
          <option value="all">All time</option>
          <option value="7d">Last 7 days</option>
          <option value="24h">Last 24 h</option>
          <option value="1h">Last hour</option>
        </select>

        <div className="flex-1" />
        <div className="text-xs text-slate-400">
          {loading ? 'Loading…' : `${count.toLocaleString()} cells`}
        </div>
      </div>

      <div className="bg-slate-900/85 border border-slate-800 rounded-md px-3 py-2 text-[11px] text-slate-300 max-w-xs" style={{ position: 'absolute', bottom: 12, left: 12, zIndex: 5 }}>
        Pan / zoom to refresh. Heatmap aggregates points to an adaptive grid.
      </div>
      <MapLegend metric={metric as LegendMetric} cellCount={count} />
    </div>
  );
}
